import type {
	MenuItemsAPIResponse,
	ProcessedAppDefinedSection,
	ProcessedMenuItem,
} from '@atlassian/navigation-bff-data-provider/universal-create';
import type { PersonalizeMode } from '@atlassian/universal-create-common/bin-param';

import type {
	AppDefinedMenuSection,
	MenuItemV2,
	NativeMenuItem,
	RenderableMenuItem,
	RenderableMenuSectionData,
} from '../../../../types/menuV2';

import { isNativeMenuItem } from './itemTypeGuards';

const filterNativeItems = (items: MenuItemV2[]): NativeMenuItem[] => items.filter(isNativeMenuItem);

/**
 * Sorts items to place promoted items at the top while preserving relative order.
 * Uses stable sort to maintain original order within promoted and non-promoted groups.
 *
 * @param items - Array of items with optional promoted flag
 * @returns New array with promoted items first, maintaining relative order
 *
 * @example
 * Input:  [A, B(promoted), C, D(promoted)]
 * Output: [B(promoted), D(promoted), A, C]
 */
const sortByPromoted = <T extends { promoted?: boolean }>(items: T[]): T[] => {
	const promotedItems: T[] = [];
	const regularItems: T[] = [];

	items.forEach((item) => {
		if (item.promoted === true) {
			promotedItems.push(item);
		} else {
			regularItems.push(item);
		}
	});

	return [...promotedItems, ...regularItems];
};

const createSection = (
	configSection: AppDefinedMenuSection,
	items: RenderableMenuItem[],
	personalized?: PersonalizeMode,
): RenderableMenuSectionData => ({
	type: 'appDefinedSection',
	heading: configSection.heading,
	foldable: configSection.foldable,
	items,
	personalized,
});

const createConfigItemsMap = (items: MenuItemV2[]): Map<string, NativeMenuItem> =>
	new Map(items.filter(isNativeMenuItem).map((item) => [item.ati.toString(), item]));

const hydrateResponseItem = (
	responseItem: ProcessedMenuItem,
	configItemsByAti: Map<string, NativeMenuItem>,
): RenderableMenuItem | null => {
	// Cross-app item: use as-is from response
	if (responseItem.type !== 'native') {
		return responseItem;
	}

	// Native item: find corresponding config item with full data
	const configItem = configItemsByAti.get(responseItem.ati);
	return configItem ?? null;
};

const hydrateSection = (
	configSection: AppDefinedMenuSection,
	responseSection: ProcessedAppDefinedSection | undefined,
): RenderableMenuSectionData | null => {
	// No response section: return native items only with promoted items at the top
	if (!responseSection) {
		const nativeItems = filterNativeItems(configSection.items);
		const sortedItems = sortByPromoted(nativeItems);
		return sortedItems.length > 0 ? createSection(configSection, sortedItems) : null;
	}

	// Use response order: map each response item to its hydrated version
	const configItemsByAti = createConfigItemsMap(configSection.items);

	const hydratedItems = responseSection.items
		.map((responseItem) => hydrateResponseItem(responseItem, configItemsByAti))
		.filter((item): item is RenderableMenuItem => item !== null);

	return hydratedItems.length > 0
		? createSection(configSection, hydratedItems, responseSection.personalized)
		: null;
};

/**
 * Hydrates menu items by matching native items from the API response with configured sections.
 *
 * This function:
 * 1. Takes the API response data and the original sections configuration
 * 2. For each app-defined section in the configuration:
 *    - Uses section metadata (heading, foldable, etc.) from config
 *    - Uses item ordering from the response
 *    - For native items: finds matching config item by ATI to preserve properties (onClick, children, etc.)
 *    - For cross-app items: uses data directly from response
 * 3. Returns hydrated sections ready for rendering
 *
 * Important: This function preserves array indices by returning null for sections that couldn't be hydrated.
 * This ensures proper alignment between config sections and hydrated sections in the caller.
 * The caller is responsible for filtering out null entries.
 *
 * @param data - The API response containing processed menu items
 * @param sections - The original sections configuration from props
 * @returns Array of renderable app-defined menu sections (may contain null for failed hydrations)
 */
export const getHydrateMenuItems = (
	data: MenuItemsAPIResponse,
	sections: AppDefinedMenuSection[],
): Array<RenderableMenuSectionData | null> => {
	const appDefinedSectionsFromResponse = data.sections.filter(
		(section): section is ProcessedAppDefinedSection => section.type === 'appDefinedSection',
	);

	// Map each config section to its hydrated version
	// Keep null entries to maintain index alignment with config sections
	return sections.map((configSection, index) =>
		hydrateSection(configSection, appDefinedSectionsFromResponse[index]),
	);
};

/**
 * Filters out all non-native items from each app-defined section.
 * Used in error fallback to show only native items.
 * Promoted items are placed at the top of each section while preserving relative order.
 *
 * @param sections - The original sections configuration from props
 * @returns Array of app-defined sections containing only native items with promoted items first
 */
export const filterNativeItemsOnly = (
	sections: AppDefinedMenuSection[],
): RenderableMenuSectionData[] =>
	sections
		.map((section) => {
			const nativeItems = filterNativeItems(section.items);
			const sortedItems = sortByPromoted(nativeItems);
			return sortedItems.length > 0 ? createSection(section, sortedItems) : null;
		})
		.filter((section): section is RenderableMenuSectionData => section !== null);
